"use client";

import { useState, useRef, useEffect } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import Image from "next/image";

const GREEN = "rgb(34 150 83)";

type Treatment = {
  id: number;
  name: string;
  tagline: string;
  description: string;
  duration: string;
  image: string;
};

const TREATMENTS: Treatment[] = [
  {
    id: 1,
    name: "Dental Implants",
    tagline: "Permanent perfection",
    description:
      "Titanium posts with natural-looking crowns for lifetime tooth replacement",
    duration: "2-3 visits",
    image: "/treatments/implantss.png",
  },
  {
    id: 2,
    name: "Laser Whitening",
    tagline: "Instant brilliance",
    description:
      "8+ shades brighter with professional laser treatment in just 60 minutes",
    duration: "1 hour",
    image: "/treatments/whitening.webp",
  },
  {
    id: 3,
    name: "Clear Aligners",
    tagline: "Invisible transformation",
    description:
      "Custom 3D-printed aligners straighten teeth without metal braces",
    duration: "12-18 months",
    image: "/treatments/clear.png",
  },
  {
    id: 4,
    name: "Root Canal Treatment",
    tagline: "Painless rescue",
    description:
      "Advanced microsurgery saves infected teeth with minimal discomfort",
    duration: "1-2 visits",
    image: "/treatments/rct.webp",
  },
  {
    id: 5,
    name: "Porcelain Veneers",
    tagline: "Hollywood smile",
    description:
      "Enhance front teeth aesthetics with slim, natural covers.",
    duration: "2 visits",
    image: "/treatments/veeners.png",
  },
  {
    id: 6,
    name: "Crowns & Bridges",
    tagline: "Strength restored",
    description:
      "Protect damaged teeth and fill gaps with durable restorations.",
    duration: "1-2 visits",
    image: "/treatments/crowns.png",
  },
  {
    id: 7,
    name: "Deep Cleaning",
    tagline: "Fresh foundation",
    description:
      "Ultrasonic scaling removes plaque and prevents gum disease",
    duration: "45 mins",
    image: "/treatments/cleaning.png",
  },
  {
    id: 8,
    name: "Gum Treatment (or Periodontal Treatment)",
    tagline: "Tissue repair",
    description:
      "Laser-assisted grafting rebuilds receding gums with fast healing",
    duration: "Multiple visits",
    image: "/treatments/gum.png",
  },
  {
    id: 9,
    name: "Pediatric Dentistry",
    tagline: "Gentle care",
    description:
      "Gentle care for kids' healthy smiles from the first tooth.",
    duration: "30-45 mins",
    image: "/treatments/kids.png",
  },
  {
    id: 10,
    name: "Emergency Care",
    tagline: "24/7 relief",
    description:
      "Immediate trauma specialists for urgent pain relief and repair",
    duration: "Immediate",
    image:
      "https://images.unsplash.com/photo-1588776814546-daab30f310ce?w=800&q=80",
  },
];

export default function Treatments() {
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });

  const [activeIndex, setActiveIndex] = useState(0);
  const controls = useAnimation();

  const gap = 24;

  const getCardWidth = () => {
    if (typeof window === "undefined") return 380;
    if (window.innerWidth < 640) return 300;
    if (window.innerWidth < 1024) return 340;
    return 380;
  };

 useEffect(() => {
  const cardWidth = getCardWidth();

  // DESKTOP — unchanged
  if (window.innerWidth >= 1024) {
    controls.start({
      x: -activeIndex * (cardWidth + gap),
    });
    return;
  }

  // MOBILE / TABLET — TRUE CENTERING
  const viewportWidth =
    viewportRef.current?.offsetWidth ?? window.innerWidth;

  const centerOffset = (viewportWidth - cardWidth) / 2;

  controls.start({
    x: centerOffset - activeIndex * (cardWidth + gap),
  });
}, [activeIndex, controls]);

  const next = () =>
    setActiveIndex((prev) =>
      Math.min(prev + 1, TREATMENTS.length - 1)
    );

  const prev = () =>
    setActiveIndex((prev) => Math.max(prev - 1, 0));

  return (
    <section
      ref={rootRef}
      id="treatments"
      className="relative overflow-hidden bg-gradient-to-b from-emerald-950 via-gray-900 to-emerald-950 py-16 sm:py-20 lg:py-24"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16 text-center"
        >
          <span className="inline-block rounded-full border-2 border-emerald-400/50 bg-emerald-500/20 px-5 py-2 text-sm font-bold text-emerald-300">
            💚 Our Treatments
          </span>
          <h2 className="mt-6 text-3xl sm:text-5xl lg:text-6xl font-black text-white">
            Transform your smile with{" "}
            <span className="bg-gradient-to-r from-emerald-400 to-cyan-300 bg-clip-text text-transparent">
              advanced care
            </span>
          </h2>
        </motion.div>

        {/* CAROUSEL */}
        <div ref={viewportRef} className="relative mx-auto max-w-[1200px]">
          <div className="overflow-hidden">
            <motion.div
              animate={controls}
              transition={{ type: "spring", stiffness: 320, damping: 38 }}
              className="flex items-center"
            >
              {TREATMENTS.map((t, i) => {
                const isActive = i === activeIndex;
                const cardWidth = getCardWidth();

                return (
                  <motion.div
                    key={t.id}
                    className="flex-shrink-0"
                    style={{ width: cardWidth, marginRight: gap }}
                    animate={{
                      scale: isActive ? 1 : 0.9,
                      opacity: isActive ? 1 : 0.45,
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <div
                      className={`relative h-[440px] sm:h-[500px] overflow-hidden rounded-3xl border-2 ${
                        isActive
                          ? "border-emerald-400"
                          : "border-emerald-500/20"
                      }`}
                      style={{
                        boxShadow: isActive
                          ? `0 25px 70px ${GREEN}55`
                          : "0 10px 30px rgba(0,0,0,0.35)",
                      }}
                    >
                      <Image
                        src={t.image}
                        alt={t.name}
                        fill
                        className="object-cover"
                      />

                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent" />

                      <div className="absolute bottom-0 p-5 text-white">
                        <h3 className="text-2xl font-black">{t.name}</h3>
                        <p className="text-emerald-300">{t.tagline}</p>

                        {isActive && (
                          <>
                            <p className="mt-2 text-sm text-gray-200">
                              {t.description}
                            </p>
                            <p className="mt-3 text-sm font-bold">
                              ⏱ {t.duration}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* NAVIGATION ARROWS */}
          <button
            onClick={prev}
            disabled={activeIndex === 0}
            className="absolute left-2 sm:left-6 top-1/2 z-30 -translate-y-1/2 grid size-12 sm:size-14 place-items-center rounded-full border-2 border-emerald-400 bg-emerald-950/90 text-xl text-white backdrop-blur disabled:opacity-30"
          >
            ←
          </button>

          <button
            onClick={next}
            disabled={activeIndex === TREATMENTS.length - 1}
            className="absolute right-2 sm:right-6 top-1/2 z-30 -translate-y-1/2 grid size-12 sm:size-14 place-items-center rounded-full border-2 border-emerald-400 bg-emerald-950/90 text-xl text-white backdrop-blur disabled:opacity-30"
          >
            →
          </button>

          {/* DOTS */}
          <div className="mt-10 flex justify-center gap-2">
            {TREATMENTS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                className="h-2 rounded-full transition-all"
                style={{
                  width: i === activeIndex ? "36px" : "10px",
                  backgroundColor: i === activeIndex ? GREEN : "#374151",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
